import { addClass, removeClass } from "@oddcamp/js-utils/src/attribute"
import { getElements } from "@oddcamp/js-utils/src/selector"
import { addEventListener } from "@oddcamp/js-utils/src/event"
import { delegateEventListener } from "@oddcamp/js-utils/src/event"
import { createFocusTrap } from "focus-trap"
import init from "../utils/init"

// Keyboard handling for the header's dropdowns and the mobile menu.
//
// Only one panel is open at a time, so a single trap is enough. focus-trap
// gives us the focus loop, Escape-to-close and returning focus to the trigger;
// the `-active` classes stay the source of truth for what is open, so the
// existing open/close logic is left alone.
let activeTrap = null

// Closes the trap from our own code. Nulling first makes `onDeactivate` a
// no-op, so this does not re-enter the caller's close function.
const releaseFocus = () => {
  if (!activeTrap) return
  const trap = activeTrap
  activeTrap = null
  trap.deactivate()
}

// `onSelfClose` runs only when focus-trap closed itself (Escape, outside
// click), and is responsible for putting the panel's classes back.
const captureFocus = (panelEl, onSelfClose) => {
  releaseFocus()
  if (!panelEl) return

  // focus-trap throws if it is activated on a container with nothing tabbable
  // in it, and a hidden panel has nothing tabbable however many elements it
  // holds. The header hides the desktop/mobile split with `display: none` and
  // the mobile menu with `visibility: hidden`, so both have to be ruled out.
  const isVisible = (el) =>
    el.getClientRects().length > 0 &&
    window.getComputedStyle(el).visibility !== `hidden`

  const tabbable = Array.from(
    panelEl.querySelectorAll(
      `a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])`
    )
  ).filter((el) => !el.disabled && isVisible(el))

  if (!tabbable.length) return

  activeTrap = createFocusTrap(panelEl, {
    escapeDeactivates: true,
    // The trigger button sits outside its own panel, so letting focus-trap
    // close on outside clicks would deactivate on mousedown and the trigger's
    // own click would immediately reopen the panel. Outside clicks stay the
    // job of the existing document handler / the trigger itself.
    clickOutsideDeactivates: false,
    allowOutsideClick: true,
    onDeactivate() {
      if (!activeTrap) return
      activeTrap = null
      onSelfClose()
    },
  })

  activeTrap.activate()
}

const setExpanded = (el, expanded) => {
  if (el) el.setAttribute(`aria-expanded`, expanded ? `true` : `false`)
}

// user nav

init(() => {
  const container = document.querySelector(`.vb-nav--portal__header__user`)
  if (!container) return

  const button = container.querySelector(`:scope > button`)

  button.addEventListener(`click`, () => container.classList.toggle(`--active`))

  document.addEventListener(`click`, (e) => {
    if (!e.target.closest(`.vb-nav--portal__header__user`)) {
      container.classList.remove(`--active`)
    }
  })

  document.addEventListener(`keyup`, (e) => {
    if (e.key == `Escape`) {
      container.classList.remove(`--active`)
    }
  })
})

// admin user nav

delegateEventListener(`.admin-bar__user button`, `click`, () => {
  addClass(`.admin-bar__user`, `--active`)
})

delegateEventListener(`.admin-bar__user button.-close`, `click`, () => {
  removeClass(`.admin-bar__user`, `--active`)
})

init(() => {
  const adminContainer = document.querySelector(`.admin-bar__user`)
  if (!adminContainer) return

  document.addEventListener(`click`, (e) => {
    if (!e.target.closest(`.admin-bar__user`)) {
      removeClass(`.admin-bar__user`, `--active`)
    }
  })

  document.addEventListener(`keyup`, (e) => {
    if (e.key == `Escape`) {
      removeClass(`.admin-bar__user`, `--active`)
    }
  })
})

// admin nav

init(() => {
  const toggleEl = document.querySelector(`.admin-bar__toggle button`)
  if (!toggleEl) return

  const toggleContainer = document.querySelector(`.admin-header`)

  toggleEl.addEventListener(`click`, () => {
    toggleEl.classList.toggle(`--active`)
    toggleContainer.classList.toggle(`--active`)
    toggleContainer.parentElement.classList.toggle(`--active`)
  })

  document.addEventListener(`click`, (e) => {
    if (
      !e.target.closest(`.admin-header`) &&
      !e.target.closest(`.admin-bar__toggle`)
    ) {
      toggleContainer.classList.remove(`--active`)
      toggleEl.classList.remove(`--active`)
      toggleContainer.parentElement.classList.remove(`--active`)
    }
  })
})

// admin nav toggles

init(() => {
  const toggleEls = getElements(`.admin-header__nav button`)
  const targetEls = getElements(`.admin-header__nav .-sub`)
  if (!toggleEls.length) return

  addEventListener(toggleEls, `click`, (e, triggerEl) => {
    const isActive = triggerEl.classList.contains(`--active`)
    removeClass(toggleEls, `--active`)
    removeClass(targetEls, `--active`)

    if (!isActive) {
      triggerEl.classList.add(`--active`)
      triggerEl.nextElementSibling.classList.add(`--active`)
    }
  })
})

init(() => {
  const vbNav = document.querySelector(`.vb-web-nav`)
  if (!vbNav) return

  const closeAllDropdowns = () => {
    releaseFocus()
    vbNav.classList.remove(`-dropdown-expanded`)
    vbNav.style.paddingBottom = null

    // Only target explicit auth/sign-in buttons; do not touch primary nav items (anchors)
    document
      .querySelectorAll(
        `.vb-web-nav__extra-links button.-js-auth-btn, .vb-web-nav .js--sign-in-btn`
      )
      .forEach((btnEl) => {
        btnEl.classList.remove(`-active`)
        if (dropdownFor(btnEl)) setExpanded(btnEl, false)

        if (
          btnEl.classList.contains(`-js-auth-btn`) ||
          btnEl.classList.contains(`js--sign-in-btn`)
        ) {
          const inactiveSpan = btnEl.querySelector(`.-auth-inactive`)
          if (inactiveSpan) {
            inactiveSpan.style.display = `inline-block`
          }
          const activeSpan = btnEl.querySelector(`.-auth-active`)
          if (activeSpan) {
            activeSpan.style.display = `none`
          }
        }
      })

    // Only collapse auth-related submenus explicitly controlled by JS
    document
      .querySelectorAll(
        `.vb-web-nav__extra-links .sub-menu, .vb-web-nav .-auth-sub-menu, .vb-web-nav .-lang-sub-menu`
      )
      .forEach((subMenu) => {
        subMenu.classList.remove(`-active`)
      })

    if (
      vbNav.classList.contains(`vb-web-nav--scrollable`) &&
      window.scrollY === 0
    ) {
      vbNav.classList.remove(`-scrolled`)
    }
  }

  if (vbNav.classList.contains(`vb-web-nav--scrollable`)) {
    // when scrolling vbNav height then add borderColor
    const scrollHandler = () => {
      if (window.scrollY > 0) {
        vbNav.classList.add(`-scrolled`)
      } else {
        vbNav.classList.remove(`-scrolled`)
      }
    }

    window.addEventListener(`scroll`, scrollHandler)
    scrollHandler()
  }

  // Desktop primary nav uses CSS hover on anchors; only attach JS to auth/sign-in buttons
  const buttons = document.querySelectorAll(
    `.vb-web-nav__extra-links button.-js-auth-btn, .vb-web-nav .js--sign-in-btn`
  )

  // The panel a button opens, if it opens one. The close button *inside* the
  // user menu carries the same class but is followed by the menu's inner
  // wrapper, so it is not a disclosure button and has no panel to trap.
  const dropdownFor = (button) => {
    const next = button.nextElementSibling

    return next && next.matches(`.sub-menu, .-auth-sub-menu`) ? next : null
  }

  buttons.forEach((button) => {
    button.addEventListener(`click`, () => {
      const wasActive = button.classList.contains(`-active`)
      closeAllDropdowns()

      if (!wasActive) {
        button.classList.add(`-active`)
        button.nextElementSibling.classList.add(`-active`)
        if (dropdownFor(button)) setExpanded(button, true)

        if (
          button.classList.contains(`-js-auth-btn`) ||
          button.classList.contains(`js--sign-in-btn`)
        ) {
          if (button.querySelector(`.-auth-inactive`)) {
            button.querySelector(`.-auth-inactive`).style.display = `none`
          }

          if (button.querySelector(`.-auth-active`)) {
            button.querySelector(`.-auth-active`).style.display = `flex`
          }
        }

        if (button.nextElementSibling.classList.contains(`-active`)) {
          vbNav.style.paddingBottom = 0
          vbNav.classList.add(`-dropdown-expanded`)

          if (
            vbNav.classList.contains(`vb-web-nav--scrollable`) &&
            window.scrollY === 0
          ) {
            vbNav.classList.toggle(`-scrolled`)
          }
        }

        // Last, so the panel is fully shown before focus moves into it.
        captureFocus(dropdownFor(button), closeAllDropdowns)
      }
    })
  })

  // Desktop hover-intent helper for primary nav to bridge any remaining gaps
  // Does NOT reintroduce click toggling; it only keeps the submenu open briefly
  // while cursor travels between the trigger and the submenu.
  const desktopMenuItems = document.querySelectorAll(
    `.vb-web-nav__nav > ul > li.-menu-item-hoverable, ul.vb-web-nav__extra-links.-on-desktop li.-menu-item-hoverable`
  )

  desktopMenuItems.forEach((li) => {
    const anchor = li.querySelector(`:scope > .nav-btn.sub-menu-toggler`)
    const submenu = li.querySelector(`:scope > .sub-menu`)
    if (!anchor || !submenu) return

    let hideTimer = null

    const open = () => {
      if (hideTimer) {
        clearTimeout(hideTimer)
        hideTimer = null
      }
      li.querySelector(`.nav-btn`).classList.add(`-hover`)
      li.classList.add(`-hover`)
    }

    const scheduleClose = () => {
      if (hideTimer) clearTimeout(hideTimer)
      hideTimer = setTimeout(() => {
        li.classList.remove(`-hover`)
        li.querySelector(`.nav-btn`).classList.remove(`-hover`)
      }, 200)
    }

    // Mouse interactions.
    //
    // Opening stays on the button alone – hovering the empty part of the <li>
    // must not open a dropdown. Closing, though, is bound to the <li>: it
    // contains both the button and the submenu, and `mouseleave` counts
    // descendants, so moving from the button to the submenu (or resting
    // anywhere inside the submenu) never registers as leaving. The submenu is
    // absolutely positioned outside the <li>'s box, which does not matter –
    // mouseleave follows the DOM tree, not the layout.
    //
    // Binding the close to the button instead made every trip to the submenu a
    // race: leaving the button always armed the timer, and the submenu's own
    // mouseenter had to win before it fired, so `-hover` could be dropped while
    // the cursor was still in the menu.
    anchor.addEventListener(`mouseenter`, open)
    submenu.addEventListener(`mouseenter`, open)
    li.addEventListener(`mouseleave`, scheduleClose)

    // Keyboard interactions
    anchor.addEventListener(`focus`, open)
    anchor.addEventListener(`blur`, scheduleClose)
    submenu.addEventListener(`focusin`, open)
    submenu.addEventListener(`focusout`, scheduleClose)
  })

  const hamburgerBtnEl = document.querySelector(`#vb-web-nav-hamburger-btn`)
  const mobileSignInBtnEl = document.querySelector(`#vb-mobile-signin-btn`)
  const mobileButtons = document.querySelectorAll(
    `.vb-web-nav .vb-web-nav__mobile button.-js-toggler`
  )

  if (!hamburgerBtnEl) return

  const mobileMenuEl = vbNav.querySelector(`.vb-web-nav__mobile`)

  // A `-js-toggler` that reveals the panel right after it. The "back to menu"
  // togglers are followed by the panel's title instead, so they are not
  // disclosure buttons and must not claim aria-expanded.
  const submenuFor = (button) => {
    const next = button.nextElementSibling

    return next &&
      next.matches(`.sub-menu-for-mobile, .sub-menu-sign-in-mobile`)
      ? next
      : null
  }

  const toggleMobileMenu = () => {
    const isCurrentlyExpanded = vbNav.classList.contains(
      `-mobile-menu-expanded`
    )

    vbNav.classList.toggle(`-mobile-menu-expanded`)
    hamburgerBtnEl
      .querySelector(`.vb-web-hamburger`)
      .classList.toggle(`is-active`)

    hamburgerBtnEl.classList.toggle(`-active`)
    setExpanded(hamburgerBtnEl, !isCurrentlyExpanded)

    if (isCurrentlyExpanded) {
      releaseFocus()
      mobileButtons.forEach((btnEl) => {
        btnEl.classList.remove(`-active`)
        if (submenuFor(btnEl)) setExpanded(btnEl, false)
      })
      setExpanded(mobileSignInBtnEl, false)
      document
        .querySelectorAll(
          `.vb-web-nav .vb-web-nav__mobile .sub-menu-for-mobile, .vb-web-nav .vb-web-nav__mobile .sub-menu-sign-in-mobile`
        )
        .forEach((subMenu) => {
          subMenu.classList.remove(`-active`)
        })
    } else {
      // Escape and outside clicks close the whole menu, which is what the
      // hamburger toggle does too.
      captureFocus(mobileMenuEl, toggleMobileMenu)
    }
  }

  hamburgerBtnEl.addEventListener(`click`, toggleMobileMenu)

  if (mobileSignInBtnEl) {
    mobileSignInBtnEl.addEventListener(`click`, () => {
      if (!vbNav.classList.contains(`-mobile-menu-expanded`)) {
        toggleMobileMenu()
      }

      const mobileMenuSignInToggler = document.querySelector(
        `.vb-web-nav__mobile .-bottom button.-js-toggler`
      )

      if (mobileMenuSignInToggler) {
        mobileButtons.forEach((btnEl) => {
          if (btnEl !== mobileMenuSignInToggler)
            btnEl.classList.remove(`-active`)
        })
        document
          .querySelectorAll(
            `.vb-web-nav .vb-web-nav__mobile .sub-menu-for-mobile, .vb-web-nav .vb-web-nav__mobile .sub-menu-sign-in-mobile`
          )
          .forEach((subMenu) => {
            if (subMenu !== mobileMenuSignInToggler.nextElementSibling)
              subMenu.classList.remove(`-active`)
          })

        mobileMenuSignInToggler.classList.add(`-active`)
        mobileMenuSignInToggler.nextElementSibling.classList.add(`-active`)
        setExpanded(mobileSignInBtnEl, true)
        if (submenuFor(mobileMenuSignInToggler)) {
          setExpanded(mobileMenuSignInToggler, true)
        }
      }
    })
  }

  mobileButtons.forEach((button) => {
    button.addEventListener(`click`, () => {
      mobileButtons.forEach((btnEl) => {
        if (btnEl !== button) {
          btnEl.classList.remove(`-active`)
          if (submenuFor(btnEl)) setExpanded(btnEl, false)
        }
      })

      document
        .querySelectorAll(
          `.vb-web-nav .vb-web-nav__mobile .sub-menu-for-mobile, .vb-web-nav .vb-web-nav__mobile .sub-menu-sign-in-mobile`
        )
        .forEach((subMenu) => {
          if (subMenu !== button.nextElementSibling)
            subMenu.classList.remove(`-active`)
        })

      button.classList.toggle(`-active`)
      button.nextElementSibling.classList.toggle(`-active`)

      // The panel stays inside the mobile menu's own focus trap, so only the
      // expanded state needs reporting. A "back to menu" toggler is followed
      // by the panel title, not a panel, so it reports nothing.
      if (submenuFor(button)) {
        setExpanded(button, button.classList.contains(`-active`))
      }
    })
  })

  document.addEventListener(`click`, (event) => {
    if (
      vbNav.classList.contains(`-dropdown-expanded`) &&
      !vbNav.querySelector(`.vb-web-nav__extra-links`).contains(event.target)
    ) {
      closeAllDropdowns()
    }
  })

  const dropdownCloseBtnEl = document.querySelector(
    `.vb-web-nav .-js-nav-close-dropdown`
  )

  if (dropdownCloseBtnEl) {
    dropdownCloseBtnEl.addEventListener(`click`, () => {
      closeAllDropdowns()
    })
  }
})
